import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
    Clock, Shield, AlertTriangle, ChevronRight, ChevronLeft,
    CheckCircle, Send, Loader2, Lock, Brain, Eye, ShieldAlert,
    Scan, Radio, Fingerprint, EyeOff, ShieldOff
} from 'lucide-react';
import { credentialsAPI } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

// ── Integrity alert config (escalating severity) ─────────────────────────
const INTEGRITY_ALERTS = {
    tabSwitch: [
        {
            severity: 'warning',
            icon: EyeOff,
            title: 'TAB SWITCH DETECTED',
            subtitle: 'Assessment Monitoring Active',
            body: "We noticed you left this tab. This credential represents YOUR knowledge — not what you can Google in 10 seconds.",
            footer: "This event has been logged to your integrity report.",
        },
        {
            severity: 'critical',
            icon: ShieldAlert,
            title: 'REPEATED TAB SWITCHING',
            subtitle: 'Integrity Score Degrading',
            body: "Multiple tab exits recorded. Every employer who views this credential will see your integrity score. Right now, it doesn't look good.",
            footer: "Tab switches: {count} · All events are timestamped and permanent.",
        },
        {
            severity: 'severe',
            icon: ShieldOff,
            title: 'INTEGRITY COMPROMISED',
            subtitle: 'Credential Value Diminished',
            body: "Persistent tab switching has been flagged. A credential with this many integrity violations tells employers more about your character than your score ever will.",
            footer: 'Your credential will display: "⚠️ Completed with significant integrity concerns"',
        },
    ],
    copy: {
        severity: 'critical',
        icon: Fingerprint,
        title: 'COPY ATTEMPT BLOCKED',
        subtitle: 'Clipboard Access Denied',
        body: "You just tried to copy text from this assessment. Ask yourself: if you need to paste a question somewhere else, do you actually know this material?",
        footer: "This credential exists to prove YOU learned it — not that you're good at searching.",
    },
    rightClick: {
        severity: 'warning',
        icon: Scan,
        title: 'CONTEXT MENU INTERCEPTED',
        subtitle: 'Suspicious Interaction Logged',
        body: "Right-click detected. If you're trying to inspect elements, translate text, or copy content — we see all of it. The question is: why would someone who actually knows this material need to?",
        footer: "Integrity monitoring is continuous and cannot be disabled.",
    },
    devTools: {
        severity: 'severe',
        icon: Radio,
        title: 'DEVELOPER TOOLS DETECTED',
        subtitle: 'Environment Anomaly Flagged',
        body: "We've detected development tools in your browser. We won't stop you — that would be pointless. But consider this: a credential earned through manipulation is a lie you'll carry into every interview, every job, every review. And lies have a way of surfacing.",
        footer: "This is the only warning. All subsequent activity is being recorded.",
    },
};

const SEVERITY_STYLES = {
    warning: {
        bg: 'from-amber-950/95 via-[#0F131F]/98 to-amber-950/95',
        border: 'border-amber-500/30',
        glow: 'shadow-[0_0_80px_rgba(245,158,11,0.15)]',
        titleColor: 'text-amber-400',
        iconBg: 'bg-amber-500/10',
        iconColor: 'text-amber-400',
        scanline: 'bg-amber-500/5',
        timerColor: 'bg-amber-500',
    },
    critical: {
        bg: 'from-red-950/95 via-[#0F131F]/98 to-red-950/95',
        border: 'border-red-500/30',
        glow: 'shadow-[0_0_100px_rgba(239,68,68,0.2)]',
        titleColor: 'text-red-400',
        iconBg: 'bg-red-500/10',
        iconColor: 'text-red-400',
        scanline: 'bg-red-500/5',
        timerColor: 'bg-red-500',
    },
    severe: {
        bg: 'from-red-950/98 via-[#0F131F]/99 to-red-950/98',
        border: 'border-red-600/40',
        glow: 'shadow-[0_0_120px_rgba(220,38,38,0.25)]',
        titleColor: 'text-red-500',
        iconBg: 'bg-red-600/15',
        iconColor: 'text-red-500',
        scanline: 'bg-red-600/8',
        timerColor: 'bg-red-600',
    },
};

// ── IntegrityAlert Overlay Component ─────────────────────────────────────
const IntegrityAlert = ({ alert, flagCount, onDismiss }) => {
    const [progress, setProgress] = useState(100);
    const [displayedText, setDisplayedText] = useState('');
    const duration = alert.severity === 'severe' ? 8000 : alert.severity === 'critical' ? 6000 : 5000;

    const style = SEVERITY_STYLES[alert.severity];
    const IconComponent = alert.icon;

    // Auto-dismiss timer
    useEffect(() => {
        const start = Date.now();
        const timer = setInterval(() => {
            const elapsed = Date.now() - start;
            const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
            setProgress(remaining);
            if (remaining <= 0) {
                clearInterval(timer);
                onDismiss();
            }
        }, 50);
        return () => clearInterval(timer);
    }, [duration, onDismiss]);

    // Typing animation for body text
    useEffect(() => {
        const text = alert.body;
        let i = 0;
        const typeTimer = setInterval(() => {
            i += 2;
            setDisplayedText(text.substring(0, i));
            if (i >= text.length) clearInterval(typeTimer);
        }, 15);
        return () => clearInterval(typeTimer);
    }, [alert.body]);

    const footerText = alert.footer.replace('{count}', flagCount);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            onClick={onDismiss}
        >
            {/* Dark backdrop with scanlines */}
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[...Array(20)].map((_, i) => (
                    <motion.div
                        key={i}
                        className={`absolute w-full h-px ${style.scanline}`}
                        style={{ top: `${i * 5}%` }}
                        animate={{ opacity: [0.3, 0.8, 0.3], x: ['-100%', '100%'] }}
                        transition={{ duration: 3, repeat: Infinity, delay: i * 0.15 }}
                    />
                ))}
            </div>

            {/* Alert card */}
            <motion.div
                initial={{ scale: 0.8, y: 30 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                onClick={(e) => e.stopPropagation()}
                className={`relative max-w-md w-full bg-gradient-to-b ${style.bg} ${style.border} border rounded-2xl overflow-hidden ${style.glow}`}
            >
                {/* Top timer bar */}
                <div className="h-1 bg-white/5 w-full">
                    <motion.div
                        className={`h-full ${style.timerColor}`}
                        style={{ width: `${progress}%` }}
                        transition={{ duration: 0.1 }}
                    />
                </div>

                {/* CRT scanline overlay */}
                <motion.div
                    className="absolute top-0 left-0 right-0 h-32 opacity-20 pointer-events-none"
                    style={{
                        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,0,0,0.03) 2px, rgba(255,0,0,0.03) 4px)',
                    }}
                    animate={{ backgroundPosition: ['0px 0px', '0px 4px'] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                />

                <div className="relative p-6 space-y-5">
                    {/* Header with pulsing icon */}
                    <div className="flex items-start gap-4">
                        <motion.div
                            className={`w-14 h-14 ${style.iconBg} rounded-2xl flex items-center justify-center shrink-0 border ${style.border}`}
                            animate={{ scale: [1, 1.08, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            <IconComponent className={`w-7 h-7 ${style.iconColor}`} />
                        </motion.div>
                        <div className="space-y-1">
                            <motion.h3
                                className={`text-sm font-black tracking-widest uppercase ${style.titleColor}`}
                                animate={{ opacity: [1, 0.6, 1] }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                            >
                                {alert.title}
                            </motion.h3>
                            <p className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                                {alert.subtitle}
                            </p>
                        </div>
                    </div>

                    {/* Body — typed out character by character */}
                    <div className="pl-1">
                        <p className="text-sm text-slate-300 leading-relaxed">
                            {displayedText}
                            <motion.span
                                className={`inline-block w-0.5 h-4 ${style.timerColor} ml-0.5 align-middle`}
                                animate={{ opacity: [1, 0] }}
                                transition={{ duration: 0.6, repeat: Infinity }}
                            />
                        </p>
                    </div>

                    {/* Footer with monitoring badge */}
                    <div className={`flex items-start gap-2 pt-3 border-t ${style.border}`}>
                        <Eye className="w-3 h-3 text-slate-600 shrink-0 mt-0.5" />
                        <p className="text-[10px] text-slate-600 font-mono leading-relaxed">
                            {footerText}
                        </p>
                    </div>

                    {/* Dismiss hint */}
                    <div className="text-center">
                        <p className="text-[9px] text-slate-700 font-mono uppercase tracking-widest">
                            Click anywhere to dismiss · Auto-closes in {Math.ceil((progress / 100) * (duration / 1000))}s
                        </p>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

// ── Main Page ────────────────────────────────────────────────────────────
const SkillAssessmentPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const roadmapId = searchParams.get('roadmap');
    const weekIndex = parseInt(searchParams.get('week') || '0', 10);

    // Quiz state
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [quizData, setQuizData] = useState(null);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState({});
    const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);

    // Timer
    const [timeLeft, setTimeLeft] = useState(900);
    const timerRef = useRef(null);
    const startTimeRef = useRef(Date.now());

    // Anti-cheat
    const [integrityFlags, setIntegrityFlags] = useState(0);
    const [activeAlert, setActiveAlert] = useState(null);
    const tabSwitchCountRef = useRef(0);
    const integrityRef = useRef(0);

    // ── Load quiz ───────────────────────────────────────────────────────
    useEffect(() => {
        const loadQuiz = async () => {
            if (!roadmapId) {
                setError('Missing roadmap ID');
                setLoading(false);
                return;
            }

            try {
                const data = await credentialsAPI.generateQuiz(roadmapId, weekIndex);
                setQuizData(data);
                setTimeLeft(data.time_limit_seconds || 900);
                startTimeRef.current = Date.now();
            } catch (err) {
                const detail = err?.response?.data?.detail || 'Failed to generate quiz';
                setError(detail);
            } finally {
                setLoading(false);
            }
        };

        loadQuiz();
    }, [roadmapId, weekIndex]);

    // ── Timer ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!quizData || submitting) return;

        timerRef.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    handleSubmit(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timerRef.current);
    }, [quizData, submitting]);

    // ── Anti-cheat: Dramatic overlay alerts ──────────────────────────────
    const showIntegrityAlert = useCallback((alertConfig) => {
        integrityRef.current += 1;
        setIntegrityFlags(integrityRef.current);
        setActiveAlert({ ...alertConfig, flagCount: integrityRef.current });
    }, []);

    useEffect(() => {
        if (!quizData) return;

        const handleVisibility = () => {
            if (document.hidden) {
                const count = tabSwitchCountRef.current;
                tabSwitchCountRef.current += 1;
                const msgIndex = Math.min(count, INTEGRITY_ALERTS.tabSwitch.length - 1);
                showIntegrityAlert(INTEGRITY_ALERTS.tabSwitch[msgIndex]);
            }
        };

        const handleCopy = (e) => {
            e.preventDefault();
            showIntegrityAlert(INTEGRITY_ALERTS.copy);
        };

        const handleContextMenu = (e) => {
            e.preventDefault();
            showIntegrityAlert(INTEGRITY_ALERTS.rightClick);
        };

        let devtoolsWarned = false;
        const handleResize = () => {
            const threshold = window.outerWidth - window.innerWidth > 160 ||
                window.outerHeight - window.innerHeight > 160;
            if (threshold && !devtoolsWarned) {
                devtoolsWarned = true;
                showIntegrityAlert(INTEGRITY_ALERTS.devTools);
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        document.addEventListener('copy', handleCopy);
        document.addEventListener('contextmenu', handleContextMenu);
        window.addEventListener('resize', handleResize);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            document.removeEventListener('copy', handleCopy);
            document.removeEventListener('contextmenu', handleContextMenu);
            window.removeEventListener('resize', handleResize);
        };
    }, [quizData, showIntegrityAlert]);

    // ── Handlers ────────────────────────────────────────────────────────
    const setAnswer = useCallback((questionId, answer) => {
        setAnswers(prev => ({ ...prev, [questionId]: answer }));
    }, []);

    const handleSubmit = useCallback(async (autoSubmit = false) => {
        if (submitting) return;
        if (!autoSubmit && !showConfirmSubmit) {
            setShowConfirmSubmit(true);
            return;
        }

        setSubmitting(true);
        setShowConfirmSubmit(false);
        clearInterval(timerRef.current);

        const timeTaken = Math.round((Date.now() - startTimeRef.current) / 1000);

        try {
            const formattedAnswers = Object.entries(answers).map(([qId, ans]) => ({
                question_id: qId,
                answer: ans,
            }));

            const result = await credentialsAPI.submitQuiz(
                quizData.quiz_id,
                roadmapId,
                weekIndex,
                formattedAnswers,
                { timeTaken, integrityFlags: integrityRef.current }
            );

            navigate('/assessment/results', {
                state: { result, quizData, timeTaken },
            });
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to submit quiz');
            setSubmitting(false);
        }
    }, [submitting, showConfirmSubmit, answers, quizData, roadmapId, weekIndex, navigate]);

    // ── Helpers ─────────────────────────────────────────────────────────
    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const answeredCount = Object.keys(answers).length;
    const totalQuestions = quizData?.questions?.length || 0;

    // ── Loading state ───────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="min-h-screen bg-[#08090c] flex items-center justify-center">
                <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-purple-500/10 rounded-2xl flex items-center justify-center mx-auto">
                        <Brain className="w-8 h-8 text-purple-400 animate-pulse" />
                    </div>
                    <div className="space-y-2">
                        <p className="text-white font-bold text-lg">Generating Your Assessment</p>
                        <p className="text-slate-400 text-sm">AI is creating personalized questions from your roadmap...</p>
                    </div>
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    // ── Error state ─────────────────────────────────────────────────────
    if (error) {
        return (
            <div className="min-h-screen bg-[#08090c] flex items-center justify-center">
                <div className="bg-[#0F131F] border border-red-500/20 rounded-2xl p-8 max-w-md text-center space-y-4">
                    <AlertTriangle className="w-12 h-12 text-red-400 mx-auto" />
                    <h2 className="text-white font-bold text-lg">Assessment Unavailable</h2>
                    <p className="text-slate-400 text-sm">{error}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="px-6 py-2.5 bg-purple-500/10 border border-purple-500/20 rounded-lg text-purple-400 text-sm hover:bg-purple-500/20 transition-colors"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    if (!quizData) return null;

    const currentQ = quizData.questions[currentQuestion];

    // ── Render ──────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-[#08090c] select-none">
            {/* Integrity alert overlay */}
            <AnimatePresence>
                {activeAlert && (
                    <IntegrityAlert
                        alert={activeAlert}
                        flagCount={activeAlert.flagCount}
                        onDismiss={() => setActiveAlert(null)}
                    />
                )}
            </AnimatePresence>

            {/* Top bar: timer + progress */}
            <div className="sticky top-0 z-50 bg-[#0B0F19]/95 backdrop-blur-md border-b border-white/5">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
                    {/* Quiz info */}
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
                            <Shield className="w-4 h-4 text-purple-400" />
                        </div>
                        <div>
                            <p className="text-xs font-mono text-slate-500 uppercase">{quizData.skill_area}</p>
                            <p className="text-sm text-white font-medium">{quizData.quiz_title}</p>
                        </div>
                    </div>

                    {/* Timer */}
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-mono text-sm ${timeLeft < 60 ? 'bg-red-500/10 text-red-400 border border-red-500/20 animate-pulse' :
                        timeLeft < 180 ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                            'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        }`}>
                        <Clock className="w-3.5 h-3.5" />
                        <span>{formatTime(timeLeft)}</span>
                    </div>

                    {/* Progress */}
                    <div className="hidden sm:flex items-center gap-2">
                        <span className="text-xs text-slate-500 font-mono">{answeredCount}/{totalQuestions}</span>
                        <div className="w-24 h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-purple-500 to-violet-400 rounded-full transition-all"
                                style={{ width: `${(answeredCount / totalQuestions) * 100}%` }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Integrity tracker bar */}
            {integrityFlags > 0 && (
                <div className="max-w-4xl mx-auto px-4 mt-3">
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border ${integrityFlags >= 3
                            ? 'bg-red-500/5 border-red-500/15'
                            : 'bg-amber-500/5 border-amber-500/10'
                            }`}
                    >
                        <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            <ShieldAlert className={`w-4 h-4 shrink-0 ${integrityFlags >= 3 ? 'text-red-400' : 'text-amber-400'
                                }`} />
                        </motion.div>
                        <div className="flex-1">
                            <p className={`text-[11px] font-mono ${integrityFlags >= 3 ? 'text-red-300/80' : 'text-amber-300/80'
                                }`}>
                                <span className="font-bold">{integrityFlags} integrity violation{integrityFlags > 1 ? 's' : ''}</span> recorded
                                {integrityFlags >= 3 && ' · Credential will be flagged'}
                            </p>
                        </div>
                        <div className={`flex gap-1 ${integrityFlags >= 3 ? 'text-red-500/40' : 'text-amber-500/40'}`}>
                            {[...Array(Math.min(integrityFlags, 5))].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className={`w-1.5 h-1.5 rounded-full ${integrityFlags >= 3 ? 'bg-red-500' : 'bg-amber-500'
                                        }`}
                                    animate={{ opacity: [0.3, 1, 0.3] }}
                                    transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                                />
                            ))}
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Question area */}
            <div className="max-w-3xl mx-auto px-4 py-8">
                {/* Question navigation dots */}
                <div className="flex gap-1.5 mb-8 justify-center flex-wrap">
                    {quizData.questions.map((q, i) => (
                        <button
                            key={q.id}
                            onClick={() => setCurrentQuestion(i)}
                            className={`w-7 h-7 rounded-full text-[10px] font-mono transition-all ${i === currentQuestion
                                ? 'bg-purple-500 text-white scale-110'
                                : answers[q.id]
                                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20'
                                    : 'bg-white/5 text-slate-500 hover:bg-white/10'
                                }`}
                        >
                            {i + 1}
                        </button>
                    ))}
                </div>

                {/* Current question */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentQ.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-6"
                    >
                        {/* Question header */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-mono text-purple-400 uppercase bg-purple-500/10 px-2 py-0.5 rounded">
                                    {currentQ.type === 'multiple_choice' ? 'MCQ' : currentQ.type === 'true_false' ? 'TRUE / FALSE' : 'SHORT ANSWER'}
                                </span>
                                <span className="text-[10px] font-mono text-slate-500">
                                    Q{currentQuestion + 1} of {totalQuestions}
                                </span>
                            </div>
                            <h2 className="text-lg sm:text-xl text-white font-semibold leading-relaxed">
                                {currentQ.question}
                            </h2>
                        </div>

                        {/* Answer choices */}
                        <div className="space-y-3">
                            {currentQ.type === 'multiple_choice' && currentQ.options?.map((option, i) => {
                                const letter = option.charAt(0);
                                const isSelected = answers[currentQ.id] === letter;
                                return (
                                    <button
                                        key={i}
                                        onClick={() => setAnswer(currentQ.id, letter)}
                                        className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all group ${isSelected
                                            ? 'bg-purple-500/10 border-purple-500/30 text-white'
                                            : 'bg-[#0F131F] border-white/5 text-slate-300 hover:border-white/15 hover:bg-[#12162a]'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isSelected
                                                ? 'bg-purple-500 text-white'
                                                : 'bg-white/5 text-slate-500 group-hover:bg-white/10'
                                                }`}>
                                                {letter}
                                            </span>
                                            <span className="text-sm">{option.substring(3).trim()}</span>
                                        </div>
                                    </button>
                                );
                            })}

                            {currentQ.type === 'true_false' && (
                                <div className="grid grid-cols-2 gap-3">
                                    {['True', 'False'].map(val => {
                                        const isSelected = answers[currentQ.id]?.toLowerCase() === val.toLowerCase();
                                        return (
                                            <button
                                                key={val}
                                                onClick={() => setAnswer(currentQ.id, val.toLowerCase())}
                                                className={`px-4 py-4 rounded-xl border text-center font-medium transition-all ${isSelected
                                                    ? 'bg-purple-500/10 border-purple-500/30 text-white'
                                                    : 'bg-[#0F131F] border-white/5 text-slate-300 hover:border-white/15'
                                                    }`}
                                            >
                                                {val}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {currentQ.type === 'short_answer' && (
                                <textarea
                                    value={answers[currentQ.id] || ''}
                                    onChange={(e) => setAnswer(currentQ.id, e.target.value)}
                                    placeholder="Type your answer here... (1-3 sentences)"
                                    rows={4}
                                    className="w-full bg-[#0F131F] border border-white/10 rounded-xl px-4 py-3 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-purple-500/30 resize-none transition-colors"
                                />
                            )}
                        </div>
                    </motion.div>
                </AnimatePresence>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-10">
                    <button
                        onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                        disabled={currentQuestion === 0}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                    </button>

                    {currentQuestion < totalQuestions - 1 ? (
                        <button
                            onClick={() => setCurrentQuestion(prev => Math.min(totalQuestions - 1, prev + 1))}
                            className="flex items-center gap-2 px-5 py-2.5 bg-purple-500/10 border border-purple-500/20 rounded-lg text-purple-400 text-sm hover:bg-purple-500/20 transition-colors"
                        >
                            Next
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            onClick={() => handleSubmit(false)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-violet-600 rounded-lg text-white text-sm font-medium hover:from-purple-500 hover:to-violet-500 transition-all disabled:opacity-50"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Submit Assessment
                        </button>
                    )}
                </div>
            </div>

            {/* Confirm submit modal */}
            <AnimatePresence>
                {showConfirmSubmit && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={() => setShowConfirmSubmit(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                            className="bg-[#0F131F] border border-white/10 rounded-2xl p-6 max-w-sm w-full space-y-4"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center">
                                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                                </div>
                                <h3 className="text-white font-bold">Submit Assessment?</h3>
                            </div>

                            <div className="space-y-2">
                                <p className="text-slate-400 text-sm">
                                    You've answered <span className="text-white font-bold">{answeredCount}</span> of{' '}
                                    <span className="text-white font-bold">{totalQuestions}</span> questions.
                                </p>
                                {answeredCount < totalQuestions && (
                                    <p className="text-amber-400/80 text-xs">
                                        ⚠️ {totalQuestions - answeredCount} questions are unanswered and will be marked as incorrect.
                                    </p>
                                )}
                                <p className="text-slate-500 text-xs mt-2">
                                    You cannot retake this assessment for 24 hours after submission.
                                </p>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowConfirmSubmit(false)}
                                    className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-slate-300 text-sm hover:bg-white/10 transition-colors"
                                >
                                    Keep Going
                                </button>
                                <button
                                    onClick={() => handleSubmit(true)}
                                    disabled={submitting}
                                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-violet-600 rounded-lg text-white text-sm font-medium hover:from-purple-500 hover:to-violet-500 transition-all disabled:opacity-50"
                                >
                                    {submitting ? 'Submitting...' : 'Submit Now'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SkillAssessmentPage;
